import React from "react";
import { motion } from "framer-motion";
import { Cloud, Briefcase, Gift } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import RotatingCoin from "@/components/RotatingCoin";

const Vision = () => {
  const { t } = useI18n();

  const points = [
    { key: "cloudgm", icon: Cloud, data: t.vision.points.cloudgm },
    { key: "cloudgm_business", icon: Briefcase, data: t.vision.points.cloudgm_business },
    { key: "rewards", icon: Gift, data: t.vision.points.rewards },
  ];

  return (
    <section
      id="vision"
      data-testid="vision-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Subtle abstract bg */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/30766684/pexels-photo-30766684.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[var(--bg-base)]/85" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-[var(--gold-bright)]">
            <span className="h-px w-8 bg-[rgba(212,175,55,0.5)]" />
            <span>{t.vision.kicker}</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="vision-title"
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight"
          >
            {t.vision.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="vision-body"
            className="mt-6 text-base sm:text-lg leading-relaxed text-[var(--text-muted)]"
          >
            {t.vision.body}
          </motion.p>

          <div className="mt-6 flex flex-wrap items-center gap-2 text-[10px] tracking-[0.24em] uppercase">
            {Object.entries(t.vision.tags).map(([k, v]) => (
              <span
                key={k}
                data-testid={`vision-tag-${k}`}
                className="rounded-full border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.05)] px-3 py-1.5 text-[var(--gold-bright)]"
              >
                {v}
              </span>
            ))}
          </div>
        </div>

        {/* Bento grid + coin */}
        <div className="mt-14 grid lg:grid-cols-12 gap-5">
          {/* Coin tile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative lg:col-span-5 rounded-2xl glow-border bg-[var(--bg-elevated)] p-8 sm:p-12 flex flex-col items-center justify-center min-h-[320px] overflow-hidden"
            data-testid="vision-coin-tile"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(212,175,55,0.18), transparent 60%)",
              }}
            />
            <RotatingCoin variant="static" size={200} testId="vision-coin" />
            <div className="mt-7 text-center relative">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">
                Cloud GM Coin
              </div>
              <div className="font-heading mt-2 text-2xl text-[var(--text-main)]">
                CGM
              </div>
            </div>
          </motion.div>

          {/* Points */}
          <div className="lg:col-span-7 grid sm:grid-cols-1 gap-5">
            {points.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.key}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`vision-point-${p.key}`}
                  className="group rounded-2xl glow-border glow-border-hover bg-[var(--bg-elevated)] p-6 sm:p-7 transition-all"
                >
                  <div className="flex items-start gap-5">
                    <div className="inline-flex shrink-0 items-center justify-center h-12 w-12 rounded-xl border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.06)] text-[var(--gold-bright)]">
                      <Icon size={22} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-[var(--text-main)]">
                        {p.data.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-muted)]">
                        {p.data.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <p
          data-testid="vision-note"
          className="mt-10 text-xs sm:text-sm text-[var(--text-muted)] italic"
        >
          {t.vision.note}
        </p>
      </div>
    </section>
  );
};

export default Vision;
